import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Pagination,
  Typography,
  Chip,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as api from "../utils/api";
import OrderDetails from "./OrderDetails";

const OrderListing = () => {
  const { t } = useTranslation();
  const currency = "Kshs.";
  const [allOrders, setAllOrders] = useState([]); // Store all orders from the API
  const [filteredOrders, setFilteredOrders] = useState([]); // Store filtered orders
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(4);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatusTab, setSelectedStatusTab] = useState(0);

  const orderStatuses = [
    "all",
    "pending",
    "preparing",
    "ready",
    "enroute",
    "failed",
    "cancelled",
    "delivered",
  ];

  // Fetch orders from the API
  const fetchOrders = () => {
    setLoading(true);
    api
      .get_orders()
      .then((response) => {
        if (!response.error) {
          const orders = response.data; // Response contains the 'data' key for orders
          setAllOrders(orders);
          setFilteredOrders(orders); // Set both allOrders and filteredOrders to initial data
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Filter orders based on selected status
  const filterOrders = (status) => {
    if (status === "all") {
      setFilteredOrders(allOrders);
    } else {
      setFilteredOrders(
        allOrders.filter((order) => order.status === status)
      );
    }
    setCurrentPage(1); // Reset to the first page
  };

  const handlePageChange = (event, selectedPage) => {
    setCurrentPage(selectedPage);
  };

  const handleStatusTabChange = (event, tabIndex) => {
    setSelectedStatusTab(tabIndex);
    const selectedStatus = orderStatuses[tabIndex];
    filterOrders(selectedStatus);
  };

  const getTotalAmount = (order) => {
    return order.total ? `${currency} ${order.total.toFixed(2)}` : `${currency} 0.00`;
  };

  const getOrderDate = (order) => {
    return order.formatted_date ? order.formatted_date : "N/A";
  };

  const getPaymentStatus = (order) => {
    return order.payment_status === "pending" ? (
      <Chip label="Pending"  size="small" />
    ) : (
      <Chip label="Completed" size="small" />
    );
  };

  const getOrderStatus = (order) => {
    return order.status ? (
      <Chip label={order.status} color="primary" size="small" />
    ) : (
      <Chip label="Unknown" color="default" size="small" />
    );
  };

  return (
    <Box sx={{ padding: "2px" }}>
      <Tabs value={selectedStatusTab} onChange={handleStatusTabChange} variant="scrollable" scrollButtons="auto">
        {orderStatuses.map((status, index) => (
          <Tab key={index} label={t(status)} />
        ))}
      </Tabs>

      {/* Loading Indicator */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ marginTop: "20px" }}>
          {filteredOrders.length === 0 ? (
            <Typography variant="h6" color="text.secondary" sx={{ width: "100%", textAlign: "center" }}>
              {t("No orders found")}
            </Typography>
          ) : (
            filteredOrders
              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
              .map((order) => (
                <Grid item xs={12} md={6} key={order.id}>

                  <Card sx={{ padding: 2 }}>
                    {/* Order Code (Title) aligned to the far left */}
                    <Typography variant="caption" sx={{ marginBottom: 1, textAlign: 'left' }}>
                      #{order.code}
                    </Typography>

                    {/* Top Row: Image and Vendor Name spaced evenly */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CardMedia
                          component="img"
                          image={order.photo}
                          alt={order.formatted_type}
                          sx={{ width: 50, height: 50, objectFit: "cover", marginRight: 2 }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', marginRight: 1 }}>
                          {order.vendor.name}
                        </Typography>
                      </Box>

                      <Typography variant="caption" sx={{ textAlign: "right" }}>
                        {getOrderStatus(order)}
                      </Typography>
                    </Box>

                    {/* Order Details: Below the image and vendor name */}
                    <CardContent sx={{ paddingTop: 0 }}>
                      <Box sx={{  marginBottom: 1 }}>
                        {/* Left side: Order Date */}
                        <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                          {getOrderDate(order)}
                        </Typography>

                        {/* Right side: Payment Status */}
                        <Typography variant="caption" sx={{ marginBottom: 1 }}>
                          Payment: {getPaymentStatus(order)}
                        </Typography>
                      </Box>

                      {/* Total and Ordered By on separate lines */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 1 }}>
                        <Typography variant="body2">
                          Total: {getTotalAmount(order)}
                        </Typography>

                        <Typography variant="body2" sx={{ textAlign: "right" }}>
                          <strong>{order.products.length} Items</strong> 
                        </Typography>
                      </Box>

                      {/* Payment Button or OrderDetails */}
                      <Box display="flex" justifyContent="center" alignItems="center" sx={{ marginTop: 2 }}>
                        <OrderDetails order={order} />
                      </Box>
                    </CardContent>
                  </Card>

                </Grid>

              ))
          )}
        </Grid>
      )}

      {/* Pagination */}
      <Pagination
        count={Math.ceil(filteredOrders.length / itemsPerPage)}
        page={currentPage}
        onChange={handlePageChange}
        color="primary"
        sx={{ marginTop: "20px" }}
      />
    </Box>
  );
};

export default OrderListing;
